import { DATA_SYMBOLS } from './alphabet.js';
import { generatePassCode } from './codec.js';
/**
 * AD-61: the bounded ISSUER, which is a different thing from the codec's minter.
 *
 * `generatePassCode` draws uniformly and may land on any of the thirty-seven check symbols. This
 * module draws through it and redraws when the result is one a wristband should not carry. The
 * separation is load-bearing rather than tidy, and it is worth stating why in both directions.
 *
 * **Why the constraint is not put inside `generatePassCode`.**
 * `packages/contracts/src/pass/__tests__/codec.test.ts` asserts that generation reaches all
 * thirty-seven check residues, and that assertion is a real regression detector: a modulus typo, or
 * a reduction that lost the top bits, shows up as an unreachable residue long before it shows up as
 * an accepted typo. Constraining the function under that test would turn it red, and relaxing its
 * expectation to thirty-three would delete the detector. So the codec is left exactly as it is and
 * the constraint lives here, where it belongs: it is a property of what this facility ISSUES, not
 * of what the format can express.
 *
 * **Why this is not the trim `alphabet.ts` warns against.** `CHECK_MODULUS` is still 37, the check
 * alphabet still has 37 symbols, `checkSymbolFor` and `parsePassCode` are untouched, and the
 * verifier still accepts a code whose check symbol is `~`. The detection proof is a statement about
 * the VERIFIER and survives intact. Only the issuer declines to draw one.
 *
 * **Why the issuer refuses four of the thirty-seven.** Crockford's five extra glyphs are `*`, `~`,
 * `$`, `=` and `U`. `U` is an ordinary letter and is kept. The other four cost twice over. Two of
 * them, `~` and `=`, are outside QR alphanumeric mode, so a code carrying one encodes in byte mode
 * and its symbol can land on a different QR version than the rest of the batch depending on the
 * error-correction level the SUPPLIER chooses, which this project does not control; and all four
 * need a keyboard layer change to type and read as print damage on the one line an operator falls
 * back to when the symbol will not scan. Redrawing costs `log2(37/33)`, about **0.165 of the
 * hundred bits**, and buys the invariant that every issued code is twenty-one characters of
 * `[0-9A-Z]`.
 */
/** The four check glyphs the issuer will not draw. `U` is kept: it is an ordinary letter. */
export const REFUSED_CHECK_SYMBOLS = '*~$=';
/** Every issued code is exactly this. Asserted over a batch rather than assumed. */
export const ISSUED_CODE_PATTERN = /^[0-9A-Z]{21}$/u;
/**
 * Substrings a wristband worn by a member of the public must not spell.
 *
 * On the on-demand path this list would be pointless: a code that reads badly is reissued at the
 * desk in five seconds. Pre-printing inverts that completely — the band is physically in a box,
 * cannot be re-minted, and the only remedy is throwing it away. The rate is not negligible either.
 * A twenty-symbol code offers eighteen positions for a three-letter substring over a 32-symbol
 * alphabet, so one specific three-letter word is expected `2200 * 18 / 32^3` times in a batch of
 * that size, which is **about 1.2 occurrences per word per run**: at least one is the expectation
 * rather than the tail.
 *
 * The list is short on purpose, and every entry is REACHABLE: Crockford excludes `I`, `L`, `O` and
 * `U` from the data alphabet, which already makes most candidates impossible to mint, and filtering
 * a substring that cannot occur is a rule that can never fire. It is also deliberately limited to
 * unambiguous vulgarity in Spanish or English rather than to identity terms, which are not slurs
 * and whose removal would be its own editorial act. The cost is about 0.004 bits.
 *
 * Versioned, and the version travels in the batch manifest, so a later batch generated under a
 * different list is identifiable rather than merely different.
 */
export const OFFENSIVE_SUBSTRINGS_VERSION = 1;
export const OFFENSIVE_SUBSTRINGS = Object.freeze([
    'ASS',
    'SEX',
    'FAG',
    'NAZ',
    'KKK',
    'FART',
    'RAPE',
    'TWAT',
    'WANK',
    'CRAP',
    'SHAG',
    'CACA',
    'CAGA',
    'PENE',
    'TETA',
    'MEAR',
]);
/** Whether a minted code spells something nobody should have to wear for a week. */
export function spellsSomethingOffensive(code) {
    return OFFENSIVE_SUBSTRINGS.some((word) => code.includes(word));
}
/**
 * How many draws before the source is concluded broken rather than unlucky.
 *
 * At the real refusal rate a single redraw is already unlikely and sixty-four consecutive ones have
 * a probability far below any number worth naming, so this bound is only ever reached by a source
 * that is not random at all.
 */
const MAX_DRAWS_PER_CODE = 64;
/** Whether a code is one this facility is willing to print on a band. */
export function isIssuableCode(code) {
    const check = code.slice(DATA_SYMBOLS);
    return !REFUSED_CHECK_SYMBOLS.includes(check) && !spellsSomethingOffensive(code);
}
/**
 * Mints one code whose check symbol is a letter or a digit and which spells nothing offensive.
 *
 * The loop is bounded. An unbounded redraw against a broken entropy source that returns a constant
 * would spin forever with no output and no diagnosis; a bound turns that into an immediate,
 * readable failure. The source is injected for the same two reasons `generatePassCode` injects it:
 * this package must not reach for a Node built-in, and the server hands it
 * `CryptoManager.generateSecureRandom`, which is the only approved source in this system.
 */
export function mintIssuablePassCode(randomBytes) {
    for (let draw = 0; draw < MAX_DRAWS_PER_CODE; draw += 1) {
        const code = generatePassCode(randomBytes);
        if (isIssuableCode(code)) {
            return code;
        }
    }
    throw new Error(`the entropy source produced ${String(MAX_DRAWS_PER_CODE)} consecutive unusable codes, which ` +
        'is not luck: it is a broken or constant random source');
}
/**
 * The format the supplier prints and the application later parses, as recorded in a manifest.
 *
 * Declared here rather than in the generator because it is a statement about the CODE, and the
 * application that accepts a band months later has to be able to compare what it enforces against
 * what the batch was minted under.
 */
export const PASS_ISSUE_FORMAT_VERSION = 1;
//# sourceMappingURL=mint.js.map